import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, MenuItem, Snackbar, Alert, Tabs, Tab,
  Card, CardContent, FormControl, InputLabel, Select, Tooltip, Badge, Link
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Save as SaveIcon,
  CloudUpload as UploadIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
  Description as DocIcon, DeleteForever as DeleteForeverIcon, FileUpload as FileUploadIcon,
  AttachFile as AttachFileIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon, Download as DownloadIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { hasPermission } from '../common/permissions';

const documentTypeLabels = {
  police_report: 'Police Report',
  drivers_licence: "Driver's Licence",
  pictures: 'Pictures',
  release_form: 'Release Form',
  quotation: 'Quotation',
  proof_of_payment: 'Proof of Payment',
  other: 'Other Document'
};

const documentTypeIcons = {
  police_report: <PdfIcon />,
  drivers_licence: <PdfIcon />,
  pictures: <ImageIcon />,
  release_form: <PdfIcon />,
  quotation: <DocIcon />,
  proof_of_payment: <PdfIcon />,
  other: <DocIcon />
};

const fieldSx = {
  '& .MuiInputBase-root': { background: 'rgba(255,255,255,0.07)', borderRadius: '10px' },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.15)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' }
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      background: '#1e293b',
      color: 'white',
      '& .MuiMenuItem-root': { color: 'white' },
      '& .MuiMenuItem-root:hover': { background: 'rgba(255,255,255,0.1)' },
      '& .MuiMenuItem-root.Mui-selected': { background: 'rgba(59,130,246,0.3)' }
    }
  }
};

const InsuranceClaims = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [claims, setClaims] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, quotation: 0, approved: 0, completed: 0, under_investigation: 0 });
  const [metadata, setMetadata] = useState({ departments: [], claimTypes: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date_received: '',
    claim_type: '',
    claim_description: '',
    quotation_1: '',
    quotation_2: '',
    quotation_3: '',
    police_report: 'N/A',
    drivers_licence: 'N/A',
    pictures: 'NO',
    release_form: 'NO',
    status: 'Open',
    pop: 'N/A',
    department_id: '',
    claimant_name: '',
    claim_value: '',
    notes: ''
  });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Document upload states
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docForm, setDocForm] = useState({ document_type: 'other', description: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const docFileRef = useRef(null);

  const [notice, setNotice] = useState({ open: false, message: '', severity: 'success' });

  const claimTypes = ['All', 'Motor', 'Property', 'Equipment', 'Fidelity', 'Livestock', 'Public Liability', 'Group Personal Accident', 'Other'];

const TEMPLATE_HEADERS = [
  'Date Claim Received', 'Claim', 'Quotation 1', 'Quotation 2', 'Quotation 3',
  'Police Technical Report', 'Driver Licence', 'Pictures', 'Release Form',
  'Status', 'POP', 'Department', 'Claimant Name', 'Claim Value'
];

const downloadTemplate = () => {
  const sample = [
    '2026-05-21', 'AGP6345', 'DENT & KNOCK', 'NEAT PANEL BEATERS', '',
    'YES', 'YES', 'YES', 'YES',
    'Open', 'YES', 'IT Services', 'John Doe', '5000.00'
  ];
  const csv = [TEMPLATE_HEADERS.map(h => `"${h}"`).join(','), sample.map(v => `"${v}"`).join(',')].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'insurance_claims_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};

  useEffect(() => {
    fetchData();
    fetchMetadata();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/insurance-claims');
      setClaims(response.data?.claims || []);
      setStats(response.data?.stats || { total: 0, open: 0, quotation: 0, approved: 0, completed: 0, under_investigation: 0 });
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const response = await api.get('/insurance-metadata');
      setMetadata(response.data || { departments: [], claimTypes: [], statuses: [] });
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  };

  const filteredClaims = useMemo(() => {
    let filtered = claims;
    const selectedType = claimTypes[activeTab];
    if (selectedType !== 'All') {
      filtered = filtered.filter(c => c.claim_type === selectedType);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(c =>
        (c.claim_number?.toLowerCase().includes(q)) ||
        (c.claim_description?.toLowerCase().includes(q)) ||
        (c.claimant_name?.toLowerCase().includes(q)) ||
        (c.claim_type?.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [claims, activeTab, search]);

  const today = new Date().toISOString().slice(0, 10);

  const isValidText = (val, min = 3) => val && val.trim().length >= min;

  const save = async () => {
    if (saving) return;

    if (!isValidText(form.claim_description) && !isValidText(form.claimant_name)) {
      setNotice({ open: true, message: 'Claim Description or Claimant Name must be at least 3 characters.', severity: 'error' });
      return;
    }
    if (form.date_received && form.date_received > today) {
      setNotice({ open: true, message: 'Date Received cannot be in the future.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        department_id: form.department_id || null,
        claim_value: form.claim_value ? Number(form.claim_value) : null,
        date_received: form.date_received || null
      };
      const response = editing
        ? await api.put(`/insurance-claims/${editing.claim_id}`, payload)
        : await api.post('/insurance-claims', payload);
      const savedClaim = response.data?.claim;
      if (savedClaim) {
        setClaims(current => editing
          ? current.map(item => item.claim_id === savedClaim.claim_id ? savedClaim : item)
          : [savedClaim, ...current]
        );
      }
      setNotice({ open: true, message: editing ? 'Claim updated.' : 'Insurance claim recorded.', severity: 'success' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to save claim.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/insurance-claims/${deleteTarget.claim_id}`);
      setClaims(current => current.filter(item => item.claim_id !== deleteTarget.claim_id));
      setNotice({ open: true, message: 'Claim deleted.', severity: 'success' });
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to delete claim.', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const quickStatusUpdate = async (claim, newStatus) => {
    try {
      await api.put(`/insurance-claims/${claim.claim_id}`, { ...claim, status: newStatus });
      setNotice({ open: true, message: `Status updated to ${newStatus}.`, severity: 'success' });
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Unable to update status.', severity: 'error' });
    }
  };

  const handleFileImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/insurance-claims/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNotice({ open: true, message: response.data?.message || 'Import successful.', severity: 'success' });
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Import failed.', severity: 'error' });
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const openNew = () => {
    setEditing(null);
    setForm({
      date_received: today,
      claim_type: claimTypes[activeTab] === 'All' ? '' : claimTypes[activeTab],
      claim_description: '',
      quotation_1: '',
      quotation_2: '',
      quotation_3: '',
      police_report: 'N/A',
      drivers_licence: 'N/A',
      pictures: 'NO',
      release_form: 'NO',
      status: 'Open',
      pop: 'N/A',
      department_id: '',
      claimant_name: '',
      claim_value: '',
      notes: ''
    });
    setDialogOpen(true);
  };

  const openEdit = (claim) => {
    setEditing(claim);
    setForm({
      date_received: claim.date_received || '',
      claim_type: claim.claim_type || '',
      claim_description: claim.claim_description || '',
      quotation_1: claim.quotation_1 || '',
      quotation_2: claim.quotation_2 || '',
      quotation_3: claim.quotation_3 || '',
      police_report: claim.police_report || 'N/A',
      drivers_licence: claim.drivers_licence || 'N/A',
      pictures: claim.pictures || 'NO',
      release_form: claim.release_form || 'NO',
      status: claim.status || 'Open',
      pop: claim.pop || 'N/A',
      department_id: claim.department_id || '',
      claimant_name: claim.claimant_name || '',
      claim_value: claim.claim_value || '',
      notes: claim.notes || ''
    });
    setDialogOpen(true);
  };

  const openDocDialog = (claim) => {
    setSelectedClaim(claim);
    setDocForm({ document_type: 'other', description: '' });
    setSelectedFile(null);
    if (docFileRef.current) docFileRef.current.value = '';
    setDocDialogOpen(true);
  };

  const uploadDocument = async () => {
    if (!docFileRef.current?.files?.[0]) {
      setNotice({ open: true, message: 'Please select a file to upload.', severity: 'error' });
      return;
    }
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('document_type', docForm.document_type);
      formData.append('description', docForm.description);
      formData.append('file', docFileRef.current.files[0]);

      const response = await api.post(`/insurance-claims/${selectedClaim.claim_id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setNotice({ open: true, message: 'Document uploaded successfully.', severity: 'success' });
      setDocDialogOpen(false);
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Failed to upload document.', severity: 'error' });
    } finally {
      setUploadingDoc(false);
      if (docFileRef.current) docFileRef.current.value = '';
      setSelectedFile(null);
    }
  };

  const deleteDocument = async (claimId, documentId) => {
    try {
      await api.delete(`/insurance-claims/${claimId}/documents/${documentId}`);
      setNotice({ open: true, message: 'Document deleted.', severity: 'success' });
      fetchData();
    } catch (error) {
      setNotice({ open: true, message: error.response?.data?.message || 'Failed to delete document.', severity: 'error' });
    }
  };

  const getDocumentIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <ImageIcon sx={{ color: '#10b981' }} />;
    if (mimeType?.includes('pdf')) return <PdfIcon sx={{ color: '#ef4444' }} />;
    return <DocIcon sx={{ color: '#3b82f6' }} />;
  };

  const renderDocumentSection = (claim) => {
    const docs = claim.documents || [];
    const hasDocs = docs.length > 0;

    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Documents:</Typography>
          {hasPermission(user, 'Insurance Claims', 'edit') && (
            <Button
              size="small"
              startIcon={<AttachFileIcon />}
              onClick={() => openDocDialog(claim)}
              sx={{ textTransform: 'none', color: '#3b82f6', fontSize: '0.75rem' }}
            >
              Add
            </Button>
          )}
        </Box>
        {hasDocs ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {docs.map((doc) => (
              <Tooltip key={doc.document_id} title={`${documentTypeLabels[doc.document_type] || doc.document_type} - ${doc.file_name}`}>
                <Chip
                  icon={getDocumentIcon(doc.mime_type)}
                  label={documentTypeLabels[doc.document_type] || 'Document'}
                  size="small"
                  sx={{
                    background: 'rgba(59,130,246,0.15)',
                    color: '#60a5fa',
                    '& .MuiChip-icon': { color: 'inherit' }
                  }}
                  onClick={() => window.open(`${process.env.REACT_APP_API_URL || ''}/storage/${doc.file_path}`, '_blank')}
                  onDelete={hasPermission(user, 'Insurance Claims', 'delete') ? () => deleteDocument(claim.claim_id, doc.document_id) : undefined}
                />
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: '#64748b', fontStyle: 'italic' }}>No documents uploaded</Typography>
        )}
      </Box>
    );
  };

  const getStatusChip = (status) => {
    const colors = {
      'Open': { bg: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
      'Quotation': { bg: 'rgba(245,158,11,0.2)', color: '#fbbf24' },
      'Approved': { bg: 'rgba(139,92,246,0.2)', color: '#a78bfa' },
      'Completed': { bg: 'rgba(16,185,129,0.2)', color: '#34d399' },
      'Under Investigation': { bg: 'rgba(239,68,68,0.2)', color: '#f87171' },
      'Need Clarification': { bg: 'rgba(107,114,128,0.2)', color: '#9ca3af' }
    };
    const style = colors[status] || colors['Open'];
    return (
      <Chip
        label={status}
        size="small"
        sx={{ background: style.bg, color: style.color, fontWeight: 'bold' }}
      />
    );
  };

  const getRequiredDocsChip = (claim) => {
    const required = [];
    if (claim.claim_type === 'Motor') {
      if (claim.police_report !== 'YES') required.push('Police Report');
      if (claim.drivers_licence !== 'YES') required.push('Driver Licence');
      if (claim.pictures !== 'YES') required.push('Pictures');
      if (claim.release_form !== 'YES') required.push('Release Form');
    }
    if (required.length === 0) return <Chip size="small" icon={<CheckCircleIcon />} label="Complete" sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#34d399' }} />;
    return <Chip size="small" icon={<WarningIcon />} label={`Missing: ${required.join(', ')}`} sx={{ bgcolor: 'rgba(245,158,11,0.2)', color: '#fbbf24' }} />;
  };

  return (
    <Box sx={{ p: 3, color: 'white' }}>
      <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>Insurance Claims</Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Claims', value: stats.total, color: '#3b82f6' },
          { label: 'Open', value: stats.open, color: '#f59e0b' },
          { label: 'Quotation', value: stats.quotation, color: '#8b5cf6' },
          { label: 'Approved', value: stats.approved, color: '#10b981' },
          { label: 'Completed', value: stats.completed, color: '#06b6d4' },
          { label: 'Under Investigation', value: stats.under_investigation, color: '#ef4444' },
        ].map((stat) => (
          <Grid item xs={6} sm={4} md={2} key={stat.label}>
            <Card sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color }}>{stat.value}</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8' }}>{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs and Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': { color: '#94a3b8', textTransform: 'none', fontWeight: 'bold' },
            '& .MuiTab-root.Mui-selected': { color: '#60a5fa' }
          }}
        >
          {claimTypes.map((type) => (
            <Tab
              key={type}
              label={type}
              sx={{ minWidth: 'auto' }}
            />
          ))}
        </Tabs>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search claims..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ ...fieldSx, minWidth: 200 }}
          />
          {hasPermission(user, 'Insurance Claims', 'add') && (
            <>
              <Button
                variant="contained"
                onClick={openNew}
                sx={{ bgcolor: '#3b82f6', textTransform: 'none', fontWeight: 'bold' }}
              >
                Record Claim
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ textTransform: 'none', color: '#3b82f6', borderColor: 'rgba(59,130,246,0.4)' }}
              >
                Download Template
              </Button>
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadIcon />}
                disabled={importing}
                sx={{ textTransform: 'none', fontWeight: 'bold', color: '#f59e0b', borderColor: 'rgba(245,158,11,0.5)', '&.Mui-disabled': { color: '#f59e0b', borderColor: 'rgba(245,158,11,0.4)', opacity: 0.8 } }}
              >
                {importing ? 'Importing...' : 'Import CSV/Excel'}
                <input type="file" hidden accept=".csv,.xlsx,.xls" onChange={handleFileImport} ref={fileInputRef} />
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Claims Table */}
      <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ background: '#1e293b' }}>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b' }}>Claim No.</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b' }}>Type</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b', minWidth: 200 }}>Description</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b' }}>Claimant</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b' }}>Date Received</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b' }}>Status</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b', minWidth: 250 }}>Documents</TableCell>
              <TableCell sx={{ color: '#94a3b8', fontWeight: 'bold', background: '#1e293b', position: 'sticky', right: 0, zIndex: 10, borderLeft: '1px solid rgba(255,255,255,0.1)' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClaims.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: '#64748b', py: 4 }}>
                  No insurance claims found.
                </TableCell>
              </TableRow>
            ) : (
              filteredClaims.map((claim) => (
                <TableRow key={claim.claim_id} hover sx={{ '&:hover': { background: 'rgba(255,255,255,0.05)' } }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{claim.claim_number}</TableCell>
                  <TableCell sx={{ color: '#94a3b8' }}>{claim.claim_type}</TableCell>
                  <TableCell sx={{ color: 'white' }}>{claim.claim_description}</TableCell>
                  <TableCell sx={{ color: '#94a3b8' }}>{claim.claimant_name}</TableCell>
                  <TableCell sx={{ color: '#94a3b8' }}>{claim.date_received}</TableCell>
                  <TableCell>{getStatusChip(claim.status)}</TableCell>
                  <TableCell>
                    <Box>
                      {getRequiredDocsChip(claim)}
                      {renderDocumentSection(claim)}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ position: 'sticky', right: 0, background: '#1e293b', borderLeft: '1px solid rgba(255,255,255,0.1)', zIndex: 5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {hasPermission(user, 'Insurance Claims', 'edit') && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => openEdit(claim)}
                          sx={{ textTransform: 'none', color: '#60a5fa', borderColor: 'rgba(96,165,250,0.4)', fontSize: '0.75rem' }}
                        >
                          Edit
                        </Button>
                      )}
                      {/* Quick Status Actions */}
                      {hasPermission(user, 'Insurance Claims', 'edit') && claim.status !== 'Approved' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => quickStatusUpdate(claim, 'Approved')}
                          sx={{ textTransform: 'none', color: '#a78bfa', borderColor: 'rgba(167,139,250,0.4)', fontSize: '0.75rem' }}
                        >
                          Approve
                        </Button>
                      )}
                      {hasPermission(user, 'Insurance Claims', 'edit') && claim.status !== 'Completed' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => quickStatusUpdate(claim, 'Completed')}
                          sx={{ textTransform: 'none', color: '#34d399', borderColor: 'rgba(52,211,153,0.4)', fontSize: '0.75rem' }}
                        >
                          Complete
                        </Button>
                      )}
                      {hasPermission(user, 'Insurance Claims', 'edit') && claim.status !== 'Under Investigation' && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => quickStatusUpdate(claim, 'Under Investigation')}
                          sx={{ textTransform: 'none', color: '#f87171', borderColor: 'rgba(248,113,113,0.4)', fontSize: '0.75rem' }}
                        >
                          Investigate
                        </Button>
                      )}
                      {hasPermission(user, 'Insurance Claims', 'delete') && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setDeleteTarget(claim)}
                          sx={{ textTransform: 'none', color: '#ef4444', borderColor: 'rgba(239,68,68,0.4)', fontSize: '0.75rem' }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>{editing ? `Edit ${editing.claim_number}` : 'Record Insurance Claim'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}><TextField fullWidth type="date" label="Date Received" InputLabelProps={{ shrink: true }} inputProps={{ max: today }} value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Claim Type" value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {metadata.claimTypes?.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}><TextField fullWidth label="Claimant Name" value={form.claimant_name} onChange={(e) => setForm({ ...form, claimant_name: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Department" value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                <MenuItem value="">None</MenuItem>
                {metadata.departments?.map(d => <MenuItem key={d.department_id} value={d.department_id}>{d.department_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Claim Description" value={form.claim_description} onChange={(e) => setForm({ ...form, claim_description: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}><TextField fullWidth type="number" label="Claim Value" value={form.claim_value} inputProps={{ min: 0 }} onChange={(e) => setForm({ ...form, claim_value: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {metadata.statuses?.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Quotation 1" value={form.quotation_1} onChange={(e) => setForm({ ...form, quotation_1: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Quotation 2" value={form.quotation_2} onChange={(e) => setForm({ ...form, quotation_2: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Quotation 3" value={form.quotation_3} onChange={(e) => setForm({ ...form, quotation_3: e.target.value })} sx={fieldSx} /></Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Police Report" value={form.police_report} onChange={(e) => setForm({ ...form, police_report: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['YES', 'NO', 'N/A'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Driver's Licence" value={form.drivers_licence} onChange={(e) => setForm({ ...form, drivers_licence: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['YES', 'NO', 'N/A'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Pictures" value={form.pictures} onChange={(e) => setForm({ ...form, pictures: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['YES', 'NO'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Release Form" value={form.release_form} onChange={(e) => setForm({ ...form, release_form: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['YES', 'NO'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField select fullWidth label="Proof of Payment" value={form.pop} onChange={(e) => setForm({ ...form, pop: e.target.value })} sx={fieldSx} SelectProps={selectMenuProps}>
                {['YES', 'NO', 'RECEIVED', 'N/A'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} sx={fieldSx} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving} sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 'bold', '&.Mui-disabled': { bgcolor: 'rgba(16,185,129,0.6)', color: 'white', opacity: 0.85 } }}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={docDialogOpen} onClose={() => setDocDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Upload Document for {selectedClaim?.claim_number}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Document Type"
                value={docForm.document_type}
                onChange={(e) => setDocForm({ ...docForm, document_type: e.target.value })}
                sx={fieldSx}
                SelectProps={selectMenuProps}
              >
                {Object.entries(documentTypeLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description (optional)" value={docForm.description} onChange={(e) => setDocForm({ ...docForm, description: e.target.value })} sx={fieldSx} />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<FileUploadIcon />}
                fullWidth
                sx={{ textTransform: 'none', py: 1.5, borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
              >
                Select File
                <input type="file" hidden ref={docFileRef} onChange={(e) => setSelectedFile(e.target.files[0] || null)} />
              </Button>
              {selectedFile && (
                <Typography variant="caption" sx={{ color: '#60a5fa', mt: 1, display: 'block' }}>
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDocDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" startIcon={<UploadIcon />} onClick={uploadDocument} disabled={uploadingDoc} sx={{ bgcolor: '#3b82f6', textTransform: 'none', fontWeight: 'bold', '&.Mui-disabled': { bgcolor: '#3b82f6', color: 'white', opacity: 0.8 } }}>{uploadingDoc ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '18px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Claim</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete claim <strong>{deleteTarget?.claim_number}</strong>?</Typography>
          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 1 }}>This will also delete all associated documents.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={remove} disabled={deleting} sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 'bold' }}>{deleting ? 'Deleting...' : 'Delete Claim'}</Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={notice.open} autoHideDuration={4000} onClose={() => setNotice({ ...notice, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={notice.severity} variant="filled" sx={{ fontWeight: 'bold', fontSize: '0.9rem', color: '#fff', bgcolor: notice.severity === 'error' ? '#ef4444' : '#10b981', '& .MuiAlert-icon': { color: '#fff' } }}>
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InsuranceClaims;
